/**
 * Shared carousel helpers for PLP product tiles and quick-view media.
 * - Touch swipe (horizontal axis lock)
 * - Adaptive arrow contrast via `.is-dark-media` on the carousel root
 *
 * Used by `product-tile.js` and `quick-view-media.js`.
 */
(function () {
  if (window.ArmadaCarouselMediaEnhancer) return;

  const SWIPE_THRESHOLD_PX = 35;
  const SWIPE_AXIS_LOCK_PX = 10;
  const SWIPE_DIRECTION_RATIO = 1.2;
  const DARK_LUMINANCE_ENTER = 0.35;
  const DARK_LUMINANCE_EXIT = 0.45;
  const SAMPLE_REGION_PX = 18;
  const SAMPLE_CANVAS_PX = 20;
  const ARROW_SAMPLE_OFFSETS = [
    { dx: 0, dy: 0 },
    { dx: -10, dy: 0 },
    { dx: 10, dy: 0 },
    { dx: 0, dy: -10 },
    { dx: 0, dy: 10 },
  ];

  let sampleCanvas = null;
  let sampleContext = null;

  const getSampleContext = () => {
    if (!sampleContext) {
      sampleCanvas = document.createElement('canvas');
      sampleCanvas.width = SAMPLE_CANVAS_PX;
      sampleCanvas.height = SAMPLE_CANVAS_PX;
      sampleContext = sampleCanvas.getContext('2d', { willReadFrequently: true });
    }
    return sampleContext;
  };

  const mapClientPointToImagePixel = (img, containerRect, clientX, clientY) => {
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    const cw = containerRect.width;
    const ch = containerRect.height;
    if (!iw || !ih || !cw || !ch) return null;

    // Matches product-tile.css: object-fit cover; object-position center
    const scale = Math.max(cw / iw, ch / ih);
    const rw = iw * scale;
    const rh = ih * scale;
    const ox = (cw - rw) / 2;
    const oy = (ch - rh) / 2;
    const cx = clientX - containerRect.left;
    const cy = clientY - containerRect.top;
    const px = (cx - ox) / scale;
    const py = (cy - oy) / scale;

    return {
      x: Math.max(0, Math.min(iw - 1, px)),
      y: Math.max(0, Math.min(ih - 1, py)),
    };
  };

  const sampleLuminance = (img, containerRect, clientPoint) => {
    const point = mapClientPointToImagePixel(img, containerRect, clientPoint.x, clientPoint.y);
    if (!point) return null;

    const sx = Math.max(0, Math.floor(point.x - SAMPLE_REGION_PX / 2));
    const sy = Math.max(0, Math.floor(point.y - SAMPLE_REGION_PX / 2));
    const sw = Math.min(SAMPLE_REGION_PX, img.naturalWidth - sx);
    const sh = Math.min(SAMPLE_REGION_PX, img.naturalHeight - sy);
    if (sw <= 0 || sh <= 0) return null;

    const ctx = getSampleContext();
    if (!ctx) return null;

    ctx.clearRect(0, 0, SAMPLE_CANVAS_PX, SAMPLE_CANVAS_PX);
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, SAMPLE_CANVAS_PX, SAMPLE_CANVAS_PX);
    const data = ctx.getImageData(0, 0, SAMPLE_CANVAS_PX, SAMPLE_CANVAS_PX).data;

    let sum = 0;
    let count = 0;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] === 0) continue;
      sum += (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
      count += 1;
    }

    return count ? sum / count : null;
  };

  const sampleArrowSideLuminance = (img, containerRect, center) => {
    const values = ARROW_SAMPLE_OFFSETS
      .map((offset) => sampleLuminance(img, containerRect, {
        x: center.x + offset.dx,
        y: center.y + offset.dy,
      }))
      .filter((value) => typeof value === 'number');

    if (!values.length) return null;

    values.sort((a, b) => a - b);
    return values[Math.floor(values.length * 0.75)];
  };

  class ArmadaCarouselMediaEnhancer {
    /**
     * @param {object} host — product-tile or quick-view-media instance
     * @param {HTMLElement|null} host.carousel
     * @param {HTMLElement[]} host.slides
     * @param {HTMLButtonElement|null} host.btnPrev
     * @param {HTMLButtonElement|null} host.btnNext
     * @param {() => number} host.getCurrentIndex
     * @param {(delta: number) => void} host.navigate
     */
    constructor(host) {
      this.host = host;
      this._contrastRaf = null;
      this._isDarkMedia = null;
      this._swipeHandlers = null;
      this._imageLoadHandlers = [];
    }

    connect() {
      if (!this.host.carousel) return;

      this._bindSwipe();
      this._bindArrowContrast();
    }

    disconnect() {
      this._unbindSwipe();
      window.cancelAnimationFrame(this._contrastRaf);
      this._contrastRaf = null;

      this._imageLoadHandlers.forEach(({ img, handler }) => {
        img.removeEventListener('load', handler);
      });
      this._imageLoadHandlers = [];
    }

    scheduleArrowContrastUpdate() {
      window.cancelAnimationFrame(this._contrastRaf);
      this._contrastRaf = window.requestAnimationFrame(() => this._updateArrowContrast());
    }

    _bindSwipe() {
      if (!('ontouchstart' in window) && !navigator.maxTouchPoints) return;

      let startX = 0;
      let startY = 0;
      let tracking = false;
      let lockedAxis = null;

      const onStart = (event) => {
        if (event.touches?.length > 1) return;
        const touch = event.touches ? event.touches[0] : event;
        startX = touch.clientX;
        startY = touch.clientY;
        tracking = true;
        lockedAxis = null;
      };

      const onMove = (event) => {
        if (!tracking || event.touches?.length !== 1) return;

        const touch = event.touches[0];
        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;

        if (!lockedAxis && (Math.abs(dx) > SWIPE_AXIS_LOCK_PX || Math.abs(dy) > SWIPE_AXIS_LOCK_PX)) {
          lockedAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }

        if (lockedAxis === 'x') {
          event.preventDefault();
        }
      };

      const onEnd = (event) => {
        if (!tracking) return;
        tracking = false;

        const touch = event.changedTouches?.[0];
        if (!touch) return;

        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;

        if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
        if (Math.abs(dx) < Math.abs(dy) * SWIPE_DIRECTION_RATIO) return;

        this.host.navigate(dx < 0 ? 1 : -1);
      };

      const onCancel = () => {
        tracking = false;
      };

      const carousel = this.host.carousel;
      carousel.addEventListener('touchstart', onStart, { passive: true });
      carousel.addEventListener('touchmove', onMove, { passive: false });
      carousel.addEventListener('touchend', onEnd, { passive: true });
      carousel.addEventListener('touchcancel', onCancel, { passive: true });

      this._swipeHandlers = { carousel, onStart, onMove, onEnd, onCancel };
    }

    _unbindSwipe() {
      if (!this._swipeHandlers) return;

      const { carousel, onStart, onMove, onEnd, onCancel } = this._swipeHandlers;
      carousel.removeEventListener('touchstart', onStart);
      carousel.removeEventListener('touchmove', onMove);
      carousel.removeEventListener('touchend', onEnd);
      carousel.removeEventListener('touchcancel', onCancel);
      this._swipeHandlers = null;
    }

    _bindArrowContrast() {
      this.host.slides.forEach((slide) => {
        const img = slide.querySelector('img');
        if (!img) return;

        const handler = () => this.scheduleArrowContrastUpdate();
        img.addEventListener('load', handler, { passive: true });
        this._imageLoadHandlers.push({ img, handler });
      });

      this.scheduleArrowContrastUpdate();
    }

    _getActiveSlideImage() {
      const slide = this.host.slides[this.host.getCurrentIndex()];
      return slide ? slide.querySelector('img') : null;
    }

    _getArrowCenters() {
      const { btnPrev, btnNext, carousel } = this.host;
      const container = carousel?.getBoundingClientRect();
      if (!container || !btnPrev || !btnNext) return null;

      const prevBox = btnPrev.getBoundingClientRect();
      const nextBox = btnNext.getBoundingClientRect();

      return {
        prev: {
          x: prevBox.left + prevBox.width / 2,
          y: prevBox.top + prevBox.height / 2,
        },
        next: {
          x: nextBox.left + nextBox.width / 2,
          y: nextBox.top + nextBox.height / 2,
        },
        container,
      };
    }

    _updateArrowContrast() {
      const { carousel } = this.host;
      if (!carousel) return;

      const img = this._getActiveSlideImage();
      const centers = this._getArrowCenters();
      if (!img || !centers || !img.complete || img.naturalWidth === 0) return;

      let lumPrev = null;
      let lumNext = null;

      try {
        lumPrev = sampleArrowSideLuminance(img, centers.container, centers.prev);
        lumNext = sampleArrowSideLuminance(img, centers.container, centers.next);
      } catch (_error) {
        // CORS-tainted canvas — fall back to light-media styling.
      }

      const samples = [lumPrev, lumNext].filter((value) => typeof value === 'number');
      if (!samples.length) {
        if (this._isDarkMedia !== false) {
          this._isDarkMedia = false;
          carousel.classList.remove('is-dark-media');
        }
        return;
      }

      const average = samples.reduce((sum, value) => sum + value, 0) / samples.length;
      const nextIsDark = this._isDarkMedia === true
        ? average < DARK_LUMINANCE_EXIT
        : average < DARK_LUMINANCE_ENTER;

      if (nextIsDark === this._isDarkMedia) return;

      this._isDarkMedia = nextIsDark;
      carousel.classList.toggle('is-dark-media', nextIsDark);
    }
  }

  window.ArmadaCarouselMediaEnhancer = ArmadaCarouselMediaEnhancer;
})();
